import toast from 'react-hot-toast';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import {
	addUpdateNewBenefitVerification,
	getBenefitVerifications,
	getBenefitVerificationsProducts,
	getIVRFile,
	getPRDocument,
	getRecentBVs,
	getRecentBvCount,
	getSingleBV,
	updateBenefitVerification,
	updateBVProduct,
	voidBenefitVerification,
} from '@/services/benefitsVerification';
import type {
	BenefitVerificationPayload,
	BVRecord,
} from '@/types/benefitsVerification';
import type { UpdateBVProductPayload } from '@/types/product';
import type { FilterType } from '@/types/table';

export const useGetBenefitVerifications = ({
	page = 1,
	limit = 15,
	enabled = true,
	columnsFilters = '[]',
	search = '',
	status = '',
	globalFilters = '[]',
	sort = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'benefitVerifications',
			page,
			limit,
			columnsFilters,
			search,
			status,
			globalFilters,
			sort,
		],
		queryFn: () =>
			getBenefitVerifications(
				page,
				limit,
				columnsFilters,
				search,
				status as string,
				globalFilters as string,
				sort as string,
			),
		enabled,
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetBenefitVerificationsProducts = ({
	page = 1,
	limit = 15,
	enabled = true,
	status = '',
	globalFilters = '[]',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'benefitVerifications',
			page,
			limit,
			status,
			globalFilters,
		],
		queryFn: () =>
			getBenefitVerificationsProducts(
				page,
				limit,
				status as string,
				globalFilters as string,
			),
		enabled,
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useCreateUpdateBV = (
	onSuccess?: () => void,
	onError?: (error: unknown) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: addUpdateNewBenefitVerification,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['benefitVerifications'] });
			onSuccess?.();
		},
		onError: (error: unknown) => {
			onError?.(error);
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to create BV. Please try again.',
			);
		},
	});
};

export const useGetSingleBV = (onSuccess?: (data: BVRecord) => void) => {
	return useMutation({
		mutationFn: getSingleBV,
		onSuccess: (data) => {
			onSuccess?.(data);
		},
		onError: (error: unknown) => {
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to fetch BV details. Please try again.',
			);
		},
	});
};

export const useGetIVRFile = (priorAuthId: string | null, enabled = true) => {
	return useQuery({
		queryKey: ['ivr-file', priorAuthId],
		queryFn: () => getIVRFile(priorAuthId as string),
		enabled: enabled && !!priorAuthId,
		retry: 1,
		staleTime: 0, // Don't cache IVR files
	});
};

export const useGetPRDocument = (
	benefitVerificationId: string | null,
	enabled = true,
) => {
	return useQuery({
		queryKey: ['pr-document', benefitVerificationId],
		queryFn: () => getPRDocument(benefitVerificationId as string),
		enabled: enabled && !!benefitVerificationId,
		retry: 1,
		staleTime: 0, // Don't cache PR documents
	});
};

export const useVoidBenefitVerification = (
	onSuccess?: () => void,
	onError?: (error: any) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: voidBenefitVerification,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['benefitVerifications'] });
			toast.success('Benefit Verification voided successfully');
			onSuccess?.();
		},
		onError: (error) => {
			onError?.(error);
			toast.error(
				(error as any)?.response?.data?.error?.message?.message ||
					'Failed to void Benefit Verification. Please try again.',
			);
		},
	});
};

export const useUpdateBenefitVerification = (
	onSuccess?: () => void,
	onError?: (errorMessage: string) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			benefitVerificationId: string;
			payload: Partial<BenefitVerificationPayload>;
		}) => updateBenefitVerification(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['benefitVerifications'] });
			toast.success('Benefit Verification Updated successfully');
			onSuccess?.();
		},
		onError: (error: {
			response?: {
				data?: {
					error?: {
						message?: string | { message?: string };
					};
				};
			};
		}) => {
			const defaultErrorMessage =
				'Failed to update Benefit Verification. Please try again.';

			const errorMessage =
				typeof error?.response?.data?.error?.message === 'object'
					? error?.response?.data?.error?.message?.message ||
						defaultErrorMessage
					: error?.response?.data?.error?.message || defaultErrorMessage;

			onError?.(errorMessage);

			toast.error(defaultErrorMessage);
		},
	});
};
export const useUpdateBVProduct = (
	onSuccess?: () => void,
	onError?: (errorMessage: string) => void,
) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			priorAuthId: string;
			payload: UpdateBVProductPayload;
		}) => updateBVProduct(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['bvproducts'] });
			onSuccess?.();
		},
		onError: (error: {
			response?: {
				data?: {
					error?: {
						message?: string | { message?: string };
					};
				};
			};
		}) => {
			const defaultErrorMessage =
				'Failed to update Benefit Verification. Please try again.';

			const errorMessage =
				typeof error?.response?.data?.error?.message === 'object'
					? error?.response?.data?.error?.message?.message ||
						defaultErrorMessage
					: error?.response?.data?.error?.message || defaultErrorMessage;

			onError?.(errorMessage);

			toast.error(defaultErrorMessage);
		},
	});
};

export function useGetRecentBVInfinite(filters: FilterType) {
  const limit = filters.limit ?? 50;

  return useInfiniteQuery(
    ["benefitVerificationsRecentInfinite", filters],
    async ({ pageParam = 1 }) => {
      // ✅ IMPORTANT: do NOT use filters.page here; infinite query controls the page
      return getRecentBVs(
        pageParam,
        limit,
        filters.globalFilters as string
      );
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        // ✅ best: totalRecords present
        const total = lastPage?.meta?.totalRecords;
        if (typeof total === "number") {
          const loaded = allPages.reduce(
            (sum, p) => sum + (p?.items?.length ?? 0),
            0
          );
          return loaded < total ? allPages.length + 1 : undefined;
        }

        // ✅ fallback: stop when page size < limit
        const lastCount = lastPage?.items?.length ?? 0;
        return lastCount === limit ? allPages.length + 1 : undefined;
      },
      staleTime: 30_000,
      keepPreviousData: true,
    }
  );
}

export const useGetRecentBV = ({
	page = 1,
	limit = 15,
	enabled = true,
	globalFilters = '[]',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'benefitVerificationsRecent',
			page,
			limit,
			globalFilters,
		],
		queryFn: () =>
			getRecentBVs(
				page,
				limit,
				globalFilters as string,
			),
		enabled,
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};

export const useGetRecentBVCount = ({
	page = 1,
	limit = 15,
	enabled = true,
	globalFilters = '[]',
	dateRangeFilter = '',
}: FilterType) => {
	return useQuery({
		queryKey: [
			'benefitVerificationsRecentCount',
			page,
			limit,
			globalFilters,
			dateRangeFilter,
		],
		queryFn: () =>
			getRecentBvCount(
				page,
				limit,
				globalFilters as string,
				dateRangeFilter as string,
			),
		enabled,
		keepPreviousData: false, // Keep showing previous data while fetching new data
		// staleTime: 30000, // Consider data fresh for 30 seconds
	});
};